/**
 * Everyone in the corpus, searchable.
 *
 * The site could already explain any law and say how each party voted on it,
 * but not what one named person did — which is the question most readers
 * actually arrive with. The record was there the whole time; there was no
 * way in.
 *
 * Filter state lives in the query string, as on the laws page, so a search
 * can be linked and the back button behaves.
 */

import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAsync, useDebounced } from "../useAsync";
import { MemberCard } from "../components/MemberCard";
import { RANKABLE, SORTS, sortBy } from "../components/party";
import { Problem, Spinner } from "../components/bits";

const PER_PAGE = 36;

export function Members() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const chamber = params.get("chamber") ?? "";
  const party = params.get("party") ?? "";
  const sort = sortBy(params.get("sort") ?? "name");
  const page = Number(params.get("page") ?? 1);

  const settled = useDebounced(query);

  const found = useAsync(
    () =>
      api.members({
        query: settled || undefined,
        chamber: chamber || undefined,
        party: party || undefined,
        sort: sort.key,
        limit: PER_PAGE,
        offset: (page - 1) * PER_PAGE,
      }),
    [settled, chamber, party, sort.key, page],
  );

  function set(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  }

  const filtered = Boolean(query || chamber || party);
  const pages = found.data ? Math.max(1, Math.ceil(found.data.total / PER_PAGE)) : 1;

  return (
    <div className="mx-auto max-w-6xl px-5 py-10">
      <h1 className="font-display text-3xl font-semibold">Who voted how</h1>
      <p className="mt-1.5 max-w-2xl text-muted">
        Everyone who cast a vote or sponsored a bill in the Congresses held here. The figure on each
        card is {sort.explains}.
      </p>

      <div className="mt-7 flex flex-wrap gap-3">
        <input
          value={query}
          onChange={(event) => set({ q: event.target.value })}
          type="search"
          aria-label="Search by name"
          placeholder="Search by name…"
          className="min-w-64 flex-1 rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm placeholder:text-faint"
        />
        <select
          value={chamber}
          onChange={(event) => set({ chamber: event.target.value })}
          aria-label="Filter by chamber"
          className="rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm"
        >
          <option value="">Both chambers</option>
          <option value="house">House</option>
          <option value="senate">Senate</option>
        </select>
        <select
          value={party}
          onChange={(event) => set({ party: event.target.value })}
          aria-label="Filter by party"
          className="rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm"
        >
          <option value="">Every party</option>
          <option value="Democrat">Democrat</option>
          <option value="Republican">Republican</option>
          <option value="Independent">Independent</option>
        </select>
        <select
          value={sort.key}
          onChange={(event) => set({ sort: event.target.value })}
          aria-label="Sort by"
          className="rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm"
        >
          {SORTS.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
        {filtered && (
          <button
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
            className="rounded-xl border border-line px-4 py-2.5 text-sm font-medium text-muted hover:text-ink"
          >
            Clear
          </button>
        )}
      </div>

      {found.data && (
        <p className="mt-5 text-sm text-muted" role="status">
          <strong className="tnum font-semibold text-ink">
            {found.data.total.toLocaleString()}
          </strong>{" "}
          {found.data.total === 1 ? "member" : "members"}
          {filtered ? " match" : ""}
          {pages > 1 && (
            <>
              {" "}
              · page <span className="tnum">{page}</span> of <span className="tnum">{pages}</span>
            </>
          )}
        </p>
      )}

      {found.error && (
        <div className="mt-6">
          <Problem error={found.error} retry={found.reload} />
        </div>
      )}
      {found.loading && !found.data && <Spinner label="Looking…" />}

      {found.data && found.data.members.length === 0 && (
        <div className="mt-8 rounded-2xl border border-line bg-card p-10 text-center">
          <p className="font-display text-xl font-semibold">Nobody matched that.</p>
          <p className="mt-2 text-muted">
            Try a surname. This covers the Congresses in the corpus, so someone who left before then
            will not be here.
          </p>
        </div>
      )}

      {found.data && found.data.members.length > 0 && (
        <div
          className={`mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 ${
            found.loading ? "opacity-60" : ""
          }`}
        >
          {found.data.members.map((member) => (
            <MemberCard key={member.bioguide} member={member} sort={sort.key} />
          ))}
        </div>
      )}

      {found.data && found.data.members.length > 0 && <Caveat sort={sort.key} />}

      {found.data && pages > 1 && (
        <nav className="mt-10 flex items-center justify-center gap-3" aria-label="Pagination">
          <button
            disabled={page <= 1}
            onClick={() => set({ page: String(page - 1) })}
            className="rounded-lg border border-line bg-card px-4 py-2 text-sm font-medium disabled:opacity-40"
          >
            ← Previous
          </button>
          <span className="tnum text-sm text-muted">
            {page} / {pages}
          </span>
          <button
            disabled={page >= pages}
            onClick={() => set({ page: String(page + 1) })}
            className="rounded-lg border border-line bg-card px-4 py-2 text-sm font-medium disabled:opacity-40"
          >
            Next →
          </button>
        </nav>
      )}
    </div>
  );
}

/**
 * What the ordering will not tell you, next to the ordering.
 *
 * Both of these are the kind of thing a reader would otherwise conclude
 * something wrong from, and neither is visible in the list itself: the
 * unranked tail looks like the worst performers, and the delegates at the
 * top of the absence list look like the least diligent members of Congress.
 */
function Caveat({ sort }: { sort: string }) {
  const how = sortBy(sort);

  // The money sorts carry their own caveats, and both are about what the
  // number is *not*. Neither is a "thin record" problem, so they come before
  // the shared one rather than inside it.
  if (sort === "lobbied") {
    return (
      <p className="mt-6 max-w-3xl text-sm leading-relaxed text-faint">
        This is only what <em>registered lobbyists</em> disclosed giving, which is a small and
        precisely named slice of the money — the whole of Congress receives on the order of $50m a
        year this way, while a single competitive Senate seat can cost more than that. Sort by
        “raised the most” for the other picture. Every figure is a floor: around a fifth of
        disclosures name a recipient who matches nobody here, usually because the money went to a
        challenger or a governor.
      </p>
    );
  }
  if (sort === "raised") {
    return (
      <p className="mt-6 max-w-3xl text-sm leading-relaxed text-faint">
        Their most recent two-year cycle, not a career total — otherwise long service would
        outrank heavy fundraising. Senators raise over six years but are shown on the same
        two-year window as everyone else, so the chambers compare like with like. Members with no
        filing on record are listed last rather than as zero.
      </p>
    );
  }

  if (!how.ranked) return null;

  if (sort === "pac") {
    return (
      <p className="mt-6 max-w-3xl text-sm leading-relaxed text-faint">
        Ranked among campaigns that raised at least $100,000. A member who raised $900 of which
        $900 came from a committee is “100% PAC funded” and belongs nowhere near the top of this
        list. Members with no filing show “—” rather than 0%: no filing is not the same as taking
        no PAC money.
      </p>
    );
  }

  return (
    <p className="mt-6 max-w-3xl text-sm leading-relaxed text-faint">
      Ranked among members with at least {RANKABLE} votes to go on. Fifteen people here served part
      of a term — one has two votes on record — and a rate drawn from a handful of votes says
      nothing, so they are listed last rather than left out.
      {sort === "absent" && (
        <>
          {" "}
          Delegates from the territories rise to the top of this one for a reason that is not
          absence: they are recorded as not voting on amendments, and on nothing else.
        </>
      )}
    </p>
  );
}
